import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usersAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorHandler';
import ConfirmModal from '../components/ConfirmModal';
import {
  HiOutlineUsers,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineX,
  HiOutlineCheck,
  HiOutlineMail,
  HiOutlineUser,
  HiOutlineLockClosed,
  HiOutlineSearch,
} from 'react-icons/hi';
import { IOSButton } from '../components/ios';
import toast from 'react-hot-toast';

function MobileAdminUsers() {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    is_active: true,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    user: null,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getAll();
      const nonAdminUsers = response.data.filter(user => !user.is_admin);
      setUsers(nonAdminUsers);
    } catch (error) {
      console.error('Error loading users:', error);
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || t('users.errorLoadingUsers'));
    } finally {
      setLoading(false);
    }
  };

  const openModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: '',
        is_active: user.is_active,
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        password: '',
        is_active: true,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      username: '',
      email: '',
      password: '',
      is_active: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.username || !formData.email) {
      toast.error(t('users.fillRequired'));
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error(t('users.passwordRequired'));
      return;
    }

    try {
      if (editingUser) {
        const updateData = {
          username: formData.username,
          email: formData.email,
          is_active: formData.is_active,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await usersAPI.update(editingUser.id, updateData);
        toast.success(t('users.userUpdated'));
      } else {
        await usersAPI.create(formData);
        toast.success(t('users.userCreated'));
      }
      closeModal();
      loadUsers();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
    }
  };

  const handleDeleteClick = (user) => {
    if (user.is_admin) {
      toast.error(t('users.cannotDeleteAdmin'));
      return;
    }
    setConfirmModal({ isOpen: true, user });
  };

  const deleteUser = async () => {
    if (!confirmModal.user) return;
    
    try {
      await usersAPI.delete(confirmModal.user.id);
      toast.success(t('users.userDeleted'));
      setConfirmModal({ isOpen: false, user: null });
      loadUsers();
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || t('users.errorDeletingUser'));
    }
  };

  const getStatusBadge = (user) => {
    if (!user.email_verified) {
      return { text: t('common.pending'), class: 'mobile-user-card__status--pending' };
    }
    if (user.is_active) {
      return { text: t('common.active'), class: 'mobile-user-card__status--active' };
    }
    return { text: t('common.inactive'), class: 'mobile-user-card__status--inactive' };
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="mobile-admin-users">
      {/* Header */}
      <div className="mobile-admin-users__header">
        <h1>{t('users.manageUsersTitle')}</h1>
        <p>{t('users.manageUsersSubtitle')}</p>
      </div>

      {/* Search */}
      <div className="mobile-admin-users__search">
        <div className="mobile-admin-users__search-input-wrapper">
          <HiOutlineSearch className="mobile-admin-users__search-icon" size={20} />
          <input
            type="text"
            className="mobile-admin-users__search-input"
            placeholder={t('users.searchUsers')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery.length > 0 && (
            <button
              className="mobile-admin-users__search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <HiOutlineX size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Add Button */}
      <div className="mobile-admin-users__actions">
        <IOSButton
          variant="primary"
          fullWidth
          onClick={() => openModal()}
          icon={HiOutlinePlus}
        >
          {t('users.newUser')}
        </IOSButton>
      </div>

      {/* Users Count */}
      <div className="mobile-admin-users__count">
        {filteredUsers.length} {filteredUsers.length === 1 ? t('users.user') : t('users.users')}
      </div>

      {/* Users List */}
      <div className="mobile-admin-users__list">
        {loading ? (
          <div className="mobile-loading">
            <div className="spinner"></div>
          </div>
        ) : filteredUsers.length > 0 ? (
          filteredUsers.map((user) => {
            const status = getStatusBadge(user);
            return (
              <div key={user.id} className="mobile-user-card">
                <div className="mobile-user-card__avatar">
                  {user.username.charAt(0).toUpperCase()}
                </div>
                <div className="mobile-user-card__info">
                  <h3 className="mobile-user-card__name">{user.username}</h3>
                  <p className="mobile-user-card__email">{user.email}</p>
                  <span className={`mobile-user-card__status ${status.class}`}>
                    {status.text}
                  </span>
                </div>
                <div className="mobile-user-card__actions">
                  <button
                    className="mobile-user-card__btn mobile-user-card__btn--edit"
                    onClick={() => openModal(user)}
                    title={t('common.edit')}
                  >
                    <HiOutlinePencil size={18} />
                  </button>
                  {!user.is_admin && (
                    <button
                      className="mobile-user-card__btn mobile-user-card__btn--delete"
                      onClick={() => handleDeleteClick(user)}
                      title={t('common.delete')}
                    >
                      <HiOutlineTrash size={18} />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="mobile-empty">
            <div className="mobile-empty__icon">
              <HiOutlineUsers size={48} />
            </div>
            <h3>{searchQuery ? t('users.noUsersFound') : t('users.noUsers')}</h3>
            <p>{searchQuery ? t('users.tryDifferentSearch') : t('users.createFirstUser')}</p>
          </div>
        )}
      </div>

      {/* Bottom spacer */}
      <div className="mobile-admin-users__bottom-spacer" />

      {/* Modal */}
      {showModal && (
        <div className="mobile-modal-overlay" onClick={closeModal}>
          <div className="mobile-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-modal__header">
              <h2>{editingUser ? t('users.editUser') : t('users.newUser')}</h2>
              <button className="mobile-modal__close" onClick={closeModal}>
                <HiOutlineX size={24} />
              </button>
            </div>

            <form className="mobile-modal__form" onSubmit={handleSubmit}>
              <div className="mobile-modal__field">
                <label>{t('common.username')} *</label>
                <div className="mobile-modal__input-wrapper">
                  <HiOutlineUser className="mobile-modal__icon" />
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder={t('common.username')}
                    required
                  />
                </div>
              </div>

              <div className="mobile-modal__field">
                <label>{t('common.email')} *</label>
                <div className="mobile-modal__input-wrapper">
                  <HiOutlineMail className="mobile-modal__icon" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder={t('common.email')}
                    required
                  />
                </div>
              </div>

              <div className="mobile-modal__field">
                <label>
                  {t('common.password')} {!editingUser && '*'}
                </label>
                <div className="mobile-modal__input-wrapper">
                  <HiOutlineLockClosed className="mobile-modal__icon" />
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingUser ? t('users.passwordPlaceholder') : t('common.password')}
                    required={!editingUser}
                  />
                </div>
              </div>

              <div className="mobile-modal__field mobile-modal__field--checkbox">
                <label className="mobile-modal__checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span className="mobile-modal__checkbox-text">{t('common.active')}</span>
                </label>
              </div>

              <div className="mobile-modal__actions">
                <IOSButton
                  type="button"
                  variant="default"
                  fullWidth
                  onClick={closeModal}
                >
                  {t('common.cancel')}
                </IOSButton>
                <IOSButton
                  type="submit"
                  variant="primary"
                  fullWidth
                >
                  {editingUser ? t('common.save') : t('users.addUser')}
                </IOSButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={t('users.deleteUser')}
        message={t('users.confirmDeleteUser', { username: confirmModal.user?.username })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        onConfirm={deleteUser}
        onCancel={() => setConfirmModal({ isOpen: false, user: null })}
        type="danger"
      />
    </div>
  );
}

export default MobileAdminUsers;
